import React from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import OrderStatus from "@/enum/OrderStatus";
import OrderTag from "@/enum/OrderTag";
import PaymentType from "@/enum/PaymentType";
import ShippingType from "@/enum/ShippingType";
import { useModalStore } from "@/hooks/Modal/hooks";
import SplitMergeParentOrderModal from "@/scenes/OrderManagement/components/dialogs/SplitMergeParentOrderDialog/SplitMergeParentOrderModal";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { useOrderSettingStore } from "@/store/hooks";
import { openUrl } from "@/utils";
import { isDropshippingOrder } from "@/utils/dropShipping";
import { checkUndeliverable, isAbnormalAddress } from "@/utils/OrderUtils";
import { Tooltip } from "antd";
import { Link } from "react-router-dom";

const useOrderInfoIcons = (order: OrderDTO, orderTagSet: Set<OrderTag>, shop: ShopDTO) => {
    const orderSettingStore = useOrderSettingStore();
    const modalStore = useModalStore();
    const orderManagementStore = useOrderManagementStore();

    const icons = [];

    const isDropShipping = isDropshippingOrder(order);

    if (isDropShipping) {
        icons.push(
            <span className="order-icon icon-unimportant" key="dropShipping">
                代发
            </span>,
        );
    }

    // 如果是代理商预约单号的订单显示tag
    if (!!order?.supplierDropShippingInfo?.ewaybillGeneratedByRetailer) {
        icons.push(
            <Tooltip key="ewaybillGeneratedByRetailer" title="代理商预约单号发货">
                <span className="order-icon icon-important">预约</span>
            </Tooltip>,
        );
    }

    if (order.isWaybillPrinted) {
        icons.push(
            <span className="inline-block" key="waybill-printed">
                <Tooltip key="kuai" title="已打印快递单">
                    <span className="order-icon icon-kuai">快</span>
                </Tooltip>

                {order.waybillPrintCount > 1 ? (
                    <Tooltip key="printCount" title={`快递单打印次数：${order.waybillPrintCount}`}>
                        <span className="order-icon icon-print-count">×{order.waybillPrintCount}</span>
                    </Tooltip>
                ) : null}
            </span>,
        );
    }

    if (order.isPackingListPrinted) {
        icons.push(
            <Tooltip key="fa" title="已打印发货单">
                <span className="order-icon icon-fa">发</span>
            </Tooltip>,
        );
    }

    if (order.isOrderItemLabelPrinted) {
        icons.push(
            <Tooltip key="biao" title="已打印商品标签">
                <span className="order-icon icon-biao">标</span>
            </Tooltip>,
        );
    }

    if (order.mergeSplitStatus === OrderMergeSplitStatus.SPLIT_CHILD) {
        icons.push(
            <Tooltip key="he" title="拆分订单">
                <span className="order-icon icon-he">拆</span>
            </Tooltip>,
        );
    }

    if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
        icons.push(
            <Tooltip
                key="he"
                title={
                    <div>
                        <p>合并订单:{order.childOrders?.length}单</p>
                        <a
                            className="text-white"
                            style={{
                                textDecoration: "underline",
                            }}
                            onClick={(e) => {
                                e.preventDefault();

                                modalStore.openModal(SplitMergeParentOrderModal, {
                                    order,
                                    onSuccess() {
                                        orderManagementStore.fetchOrders(order.shopId);
                                    },
                                });
                            }}
                        >
                            拆分合并订单
                        </a>
                    </div>
                }
            >
                <span
                    className="order-icon icon-he"
                    onClick={() => {
                        openUrl("https://qw15p3s8hv.feishu.cn/wiki/JG6mwzzDxi38ILkJmbycILdLnVF?from=from_copylink");
                    }}
                >
                    合
                </span>
            </Tooltip>,
        );
    }

    if (order.isPartShipped) {
        icons.push(
            <span className="order-icon icon-important icon-part-shipped" key="partShipped">
                部分发货
            </span>,
        );
    }

    if (order.isRefunding) {
        icons.push(
            <span className="order-icon icon-important icon-part-refunding" key="refunding">
                退款中
            </span>,
        );
    }

    if (order.preShipped) {
        icons.push(
            <span className="order-icon icon-important icon-pre-shipped" key="preShipped">
                预发货
            </span>,
        );
    }

    if (order.shippingType === ShippingType.EMS) {
        icons.push(
            <span className="order-icon icon-ems" key="ems">
                EMS
            </span>,
        );
    } else if (order.shippingType === ShippingType.POST) {
        icons.push(
            <span className="order-icon icon-post" key="post">
                平邮
            </span>,
        );
    } else if (order.shippingType === ShippingType.SELF_PICKUP) {
        icons.push(
            <span className="order-icon icon-self-pickup" key="selfPickup">
                自提
            </span>,
        );
    } else if (order.shippingType === ShippingType.INTRA_CITY) {
        icons.push(
            <span className="order-icon icon-intra-city" key="intraCity">
                同城配送
            </span>,
        );
    }

    if (order.paymentType === PaymentType.COD) {
        icons.push(
            <span className="order-icon icon-important icon-cod" key="cod">
                货到付款
            </span>,
        );
    }

    const isOrderAbnormalAddress = isAbnormalAddress(order, orderSettingStore.addressFilterKeywords);
    if (isOrderAbnormalAddress) {
        icons.push(
            <Tooltip
                styles={{ root: { maxWidth: 320 } }}
                title={
                    <Link
                        to="/setting/addressWarning"
                        className="text-white"
                        style={{ textDecoration: "underline", whiteSpace: "nowrap" }}
                    >
                        地址异常，请注意处理(点击可查看设置)
                    </Link>
                }
                key="abnormalAddress"
            >
                <span className="order-icon icon-important icon-abnormal-address">地址异常</span>
            </Tooltip>,
        );
    }

    const undeliverableReason = checkUndeliverable(order, orderSettingStore.undeliverableAreaCodes);

    if (undeliverableReason) {
        icons.push(
            <Tooltip
                title={
                    <Link
                        to="/setting/undeliverableArea"
                        className="text-white"
                        style={{ textDecoration: "underline" }}
                    >
                        快递不可达:{undeliverableReason}
                    </Link>
                }
                key="undeliverableReason"
            >
                <span className="order-icon icon-important icon-undeliverable-address">不可达</span>
            </Tooltip>,
        );
    }

    const hasGift = order?.orderItems?.some(
        (orderItem) => orderItem.status !== OrderStatus.CANCELED && orderItem.isGift,
    );

    if (hasGift) {
        icons.push(
            <Tooltip title="有赠品" key="hasGift">
                <span className="order-icon icon-has-gift" key="hasGift" title="有赠品">
                    有赠品
                </span>
            </Tooltip>,
        );
    }

    return icons;
};

export default useOrderInfoIcons;
